import { useState } from 'react';
import type { ReactElement } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { EnvironmentBanner } from './EnvironmentBanner';
import { Header } from './Header';
import { NavRail } from './NavRail';

/**
 * The frame.
 *
 * A fixed header, a fixed rail, and a scrolling main region — so the page never
 * jumps and the navigation never moves. Below `lg` the rail slides in over the
 * content and closes on selection; there is no separate mobile navigation to
 * learn.
 */

export function AppFrame(): ReactElement {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="flex min-h-dvh flex-col bg-paper">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-30 focus:rounded focus:bg-ink focus:px-3 focus:py-2 focus:text-ink-inverse"
      >
        Skip to content
      </a>

      {/* Inside the sticky region, above the header: on a system that is not
          production that fact should not scroll away. */}
      <div className="sticky top-0 z-20">
        <EnvironmentBanner />
        <Header navOpen={navOpen} onToggleNav={() => setNavOpen((open) => !open)} />
      </div>

      <div className="flex flex-1">
        {/* Rail: permanent from lg up, overlay below it. */}
        <div className="hidden w-rail shrink-0 lg:block">
          <div className="sticky top-header h-[calc(100dvh-theme(spacing.header))]">
            <NavRail />
          </div>
        </div>

        {navOpen && (
          <div className="fixed inset-0 top-header z-20 lg:hidden">
            <button
              type="button"
              aria-label="Hide sections"
              onClick={() => setNavOpen(false)}
              className="absolute inset-0 bg-ink/40"
            />
            <div className="relative h-full w-rail border-r border-ink">
              <NavRail onNavigate={() => setNavOpen(false)} />
            </div>
          </div>
        )}

        <main id="main" className="min-w-0 flex-1">
          {/* Keying on the path resets scroll-dependent state between sections. */}
          <div key={location.pathname} className="mx-auto max-w-[1400px] p-3 sm:p-4">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
